import React from 'react';
import { TrendingUp, Target, Handshake } from 'lucide-react';
import { ProgressBar } from '../components/ui.jsx';
import { stages, money } from '../data.js';

export default function Reports({ deals = [], partners = [], products = [] }) {
  const won = deals.filter((d) => d.stage === 'Won');
  const lost = deals.filter((d) => d.stage === 'Lost');
  const closed = won.length + lost.length;
  const conversion = closed ? Math.round((won.length / closed) * 100) : 0;
  const totalRevenue = won.reduce((s, d) => s + d.value, 0);
  const openPipeline = deals.filter((d) => !['Won', 'Lost'].includes(d.stage)).reduce((s, d) => s + d.value, 0);
  const funnelStages = stages.filter((s) => s !== 'Lost');
  const maxFunnel = Math.max(...funnelStages.map((s) => deals.filter((d) => d.stage === s).length), 1);

  const byRep = {};
  deals.forEach((d) => { byRep[d.owner] = (byRep[d.owner] || 0) + (d.stage === 'Won' ? d.value : 0); });

  const partnersWithRevenue = partners.map((p) => ({
    ...p,
    closedRevenue: deals.filter((d) => d.source === p.name && d.stage === 'Won').reduce((s, d) => s + d.value, 0),
  }));
  const topPartners = [...partnersWithRevenue].sort((a, b) => b.closedRevenue - a.closedRevenue).slice(0, 5);
  const maxPartnerRev = Math.max(...topPartners.map((p) => p.closedRevenue), 1);

  return (
    <>
      <div className="page-head">
        <div className="page-head-left">
          <div className="page-title">Reports</div>
          <div className="page-subtitle">Pipeline, revenue and partner performance at a glance.</div>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="stat-card-value">{conversion}%</div><div className="stat-card-label">Win rate ({won.length} of {closed} closed)</div></div>
        <div className="stat-card"><div className="stat-card-value">{money(totalRevenue)}</div><div className="stat-card-label">Revenue closed-won</div></div>
        <div className="stat-card"><div className="stat-card-value">{money(deals.filter((d) => !['Won', 'Lost'].includes(d.stage)).reduce((s, d) => s + d.value * d.probability / 100, 0))}</div><div className="stat-card-label">Weighted forecast</div></div>
        <div className="stat-card"><div className="stat-card-value">{money(openPipeline)}</div><div className="stat-card-label">Open pipeline value</div></div>
      </div>

      <div className="card">
        <div className="card-header"><div><div className="card-title">Pipeline funnel</div><div className="card-subtitle">Deal count by stage</div></div></div>
        <div className="card-body">
          {funnelStages.map((s) => {
            const count = deals.filter((d) => d.stage === s).length;
            return (
              <div key={s} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{s}</span><span className="font-mono text-muted">{count}</span>
                </div>
                <ProgressBar pct={(count / maxFunnel) * 100} tone="info" />
              </div>
            );
          })}
        </div>
      </div>

      <section className="two-col-grid">
        <div className="card">
          <div className="card-header"><div><div className="card-title">Top partners by closed revenue</div></div></div>
          <div className="card-body">
            {topPartners.map((p) => (
              <div key={p.id} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{p.name}</span><span className="font-mono text-muted">{money(p.closedRevenue)}</span>
                </div>
                <div className="progress-track"><div className="progress-bar" style={{ width: `${(p.closedRevenue / maxPartnerRev) * 100}%`, background: p.color }} /></div>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><div><div className="card-title">Revenue by product</div></div></div>
          <div className="card-body">
            {[...products].filter((p) => p.arr > 0).sort((a, b) => b.arr - a.arr).map((p) => (
              <div key={p.id} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{p.name}</span><span className="font-mono text-muted">{money(p.arr)}</span>
                </div>
                <div className="progress-track"><div className="progress-bar" style={{ width: `${(p.arr / Math.max(...products.map((x) => x.arr))) * 100}%`, background: p.color }} /></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
